/** Text helpers. Deliberately DOM-free so they run on the server too. */
import {
  MAX_BLOCK_NEWLINES,
  richRunsToText,
  scanRichText,
} from "../domain/richTextScanner.js";

export function countWords(text: string): number {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

/**
 * Server-safe HTML-to-text that preserves the writing surface's structure.
 *
 * Break rules live in one place — `scanRichText` in
 * `domain/richTextScanner.ts` — and are shared with `parseRichRuns`, so the
 * stored text and the PDF's styled runs cannot disagree on break counts. That
 * model is:
 *
 * - a block with visible text emits its text plus "\n\n" (a paragraph break);
 * - a block with no visible text (only <br>, &nbsp;, or whitespace) emits "\n";
 * - a <br> inside a text block emits "\n" (a soft break);
 * - whitespace-only lines become empty lines;
 * - leading/trailing newlines are trimmed;
 * - runs longer than four newlines collapse to four (a bounded cap).
 *
 * The PDF draws (N - 1) blank lines for a run of N newlines, so two normal
 * paragraphs draw one blank line, one empty paragraph draws two, two empty
 * paragraphs draw three, and three or more empty paragraphs stay capped at
 * three. A <br><br> inside one paragraph is also "\n\n", i.e. one blank line.
 *
 * This function is the scan's concatenated text with presentation-level
 * horizontal whitespace cleanup applied.
 */
export function htmlToStructuredText(html: string): string {
  if (typeof html !== "string" || html.length === 0) return "";
  return normalizeStructuredText(richRunsToText(scanRichText(html)));
}

function normalizeStructuredText(text: string): string {
  return text
    .split("\n")
    .map((line) =>
      line.trim() === "" ? "" : line.replace(/[ \t]+/g, " ").replace(/[ \t]+$/, ""),
    )
    .join("\n")
    .replace(new RegExp(`\\n{${MAX_BLOCK_NEWLINES + 1},}`, "g"), "\n".repeat(MAX_BLOCK_NEWLINES))
    .replace(/^\n+/, "")
    .replace(/\n+$/, "")
    .trim();
}
